'use client';

import { Typography, Box, Grid } from '@mui/material';
import { GeoIcon2, MailIcon, PhoneIcon } from '@/components';
import { COLORS, PATHS } from '@/constants';
import {
  BrokerCardContainer,
  LogoContainer,
  StatsContainer,
  StatItem,
  StatValue,
  StatLabel,
  ContactInfo,
} from './styles';
import { formatDateIfValid } from '@/common';
import { Link } from '@/components/shared/UI/Link';
import NextLink from 'next/link';
import { formatPhone } from '../../utils';
import { useImageObserver } from '@/hooks/useImageObserver';
import { pushClickMail } from '@/lib/analytics';
import brokerPlaceholder from '@/assets/svg/broker-placeholder.svg';
import { useTranslations } from 'next-intl';

interface PhoneObject {
  countryCode?: string;
  prefix?: string;
  number?: string;
  directDialNumber?: string | null;
  formatted?: string;
  telUri?: string;
}

export interface BrokerCardData {
  id: string;
  name: string;
  logo?: string;
  activePropertiesCount?: number;
  lastActiveDate?: string;
  email?: string;
  phone?: string | PhoneObject;
  address: string;
  slug: string;
  directLink?: string;
}

interface BrokerCardProps {
  broker: BrokerCardData;
}

export const BrokerCard = ({ broker }: BrokerCardProps) => {
  const _t = useTranslations();
  const { imgSrc, handleError, handleLoad } = useImageObserver(broker.logo);

  const slugLink = broker?.directLink ?? broker?.slug;

  const phoneDisplay = formatPhone(broker.phone);
  const phoneHref = phoneDisplay.replace(/\s/g, '');

  return (
    <BrokerCardContainer elevation={0}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: { xs: '12px', sm: '24px' },
            background: '#F4F5FA',
            padding: { xs: '12px', sm: '20px' },
          }}
        >
          <LogoContainer>
            <img
              src={imgSrc ?? brokerPlaceholder.src}
              alt={broker.name}
              onError={handleError}
              onLoad={handleLoad}
            />
          </LogoContainer>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
            <NextLink
              href={`/${PATHS.BROKERS}/${slugLink}`}
              style={{ width: '100%', textDecoration: 'none' }}
            >
              <Typography
                component="span"
                fontWeight={700}
                fontSize={18}
                lineHeight="130%"
                color={COLORS['primary/600']}
                sx={{
                  wordBreak: 'break-word',
                  display: 'inline-block',
                  width: '100%',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {broker.name}
              </Typography>
            </NextLink>
            {(broker.activePropertiesCount !== undefined || broker.lastActiveDate) && (
              <StatsContainer>
                {broker.activePropertiesCount !== undefined && (
                  <StatItem>
                    <StatValue>{broker.activePropertiesCount}</StatValue>
                    <StatLabel>
                      {_t.rich('brokerListPage.activeObjectsLabel', {
                        br: () => <br />,
                      })}
                    </StatLabel>
                  </StatItem>
                )}
                {broker.lastActiveDate && (
                  <StatItem>
                    <StatValue>{formatDateIfValid(broker.lastActiveDate)}</StatValue>
                    <StatLabel>
                      {_t.rich('brokerListPage.lastActiveLabel', {
                        br: () => <br />,
                      })}
                    </StatLabel>
                  </StatItem>
                )}
              </StatsContainer>
            )}
          </Box>
        </Box>

        <Grid
          container
          spacing={1.5}
          sx={{
            padding: { xs: '0 12px 12px 12px', sm: '0 20px 20px 20px' },
            display: 'grid',
            gridTemplateColumns: '1fr',
            gridTemplateRows: 'auto auto',
            gap: '6px',
          }}
        >
          <Grid>
            <ContactInfo>
              <MailIcon />
              <Typography
                fontSize={14}
                lineHeight="155%"
                color={COLORS['neutral/600']}
                sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                <b>{_t('brokerListPage.emailLabel')}:</b>{' '}
                {broker.email ? (
                  <Link
                    href={`mailto:${broker.email}`}
                    onClick={() => {
                      if (broker.email) pushClickMail(broker.email);
                    }}
                  >
                    {broker.email}
                  </Link>
                ) : (
                  '—'
                )}
              </Typography>
            </ContactInfo>
          </Grid>

          <Grid>
            <ContactInfo>
              <PhoneIcon />
              <Typography fontSize={14} lineHeight="155%" color={COLORS['neutral/600']}>
                <b>{_t('brokerListPage.phoneLabel')}:</b>{' '}
                {phoneDisplay ? <Link href={`tel:${phoneHref}`}>{phoneDisplay}</Link> : '—'}
              </Typography>
            </ContactInfo>
          </Grid>

          <Grid>
            <ContactInfo>
              <GeoIcon2 />
              <Typography
                fontSize={14}
                lineHeight="155%"
                color={COLORS['neutral/600']}
                sx={{ wordBreak: 'break-word' }}
              >
                {broker.address || '—'}
              </Typography>
            </ContactInfo>
          </Grid>
        </Grid>
      </Box>
    </BrokerCardContainer>
  );
};
